/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CloudConnectivityDetail } from "kontrolEdge/components/CloudConnectivity/CloudDetails.tsx/CloudConnectivityDetails";
import { CCDetailSection } from "kontrolEdge/components/CloudConnectivity/CloudDetails.tsx/CloudConnectivityDetailsData";
import { DatacenterDetailSection } from "kontrolEdge/components/DataCenter/DataCenterDetails/DatacenterDetailsData";
import { InternetDetailSection } from "kontrolEdge/components/Internet/InternetDetails/InternetDetailsData";
import { PortDetailSection } from "kontrolEdge/components/Port/PortDetails/PortDetails";
import { formatDataRate } from "kontrolEdge/helpers/formatDataRate";
import { ReactComponent as ConsoleConnectIcon } from "../../shared/assets/Logos/ConsoleConnect.svg";
import { ReactComponent as EquinixIcon } from "../../shared/assets/Logos/Equinix.svg";
import { ReactComponent as MegaportIcon } from "../../shared/assets/Logos/Megaport.svg";
import { formatDate } from "kontrolEdge/helpers/formattersShappedData";
import { BgpNeighbor } from "kontrolEdge/components/Internet/InternetDetails/InternetDetails";

// Arrays of options used inside the forms
export const contractOptions = [
  { value: "option1", label: "12 Months - $300 / month", term: 12, price: 300 },
  { value: "option2", label: "24 Months - $200 / month", term: 24, price: 200 },
  { value: "option3", label: "36 Months - $100 / month", term: 36, price: 100 },
];

export const dropdownItems = [
  { value: "qro1", label: "KIO QRO1 (Querétaro, México)" },
  { value: "mex2", label: "KIO MEX2 (Ciudad de México, México)" },
  { value: "mex5", label: "KIO MEX5 (Ciudad de México, México)" },
  { value: "mex6", label: "KIO MEX6 (Ciudad de México, México)" },
];

export const capacityOptions = [
  { value: "10000", label: "10 G" },
  { value: "100000", label: "100 G" },
];

export const portType: { value: "single" | "dual"; label: string }[] = [
  { value: "single", label: "Single port (Stand Alone)" },
  { value: "dual", label: "Dual port (LAG for redundancy)" },
];

//---------Mapping for Port Details Rendering inside the cards---------//
export const mapFetchedDataToPortDetails = (
  fetchedData: any,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  externalPortId?: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  externalSubid?: string
): PortDetailSection[] => {
  const allocatedObj = fetchedData.allocated || {};
  const allocatedSum = Object.values(allocatedObj).reduce(
    (acc: number, cur: any) => {
      // Convert each value to a number, defaulting to 0 if not a valid number
      const num = Number(cur) || 0;
      return acc + num;
    },
    0
  );
  // Calculate available by subtracting the allocated sum from the capacity.
  // Make sure capacityMbps is a number.
  const capacity = Number(fetchedData.capacityMbps) || 0;
  const available = capacity - allocatedSum;

  //Removes KIO text from crossConnect details
  const fullDetails = fetchedData?.crossConnect?.details ?? "";
  // Split on comma, take only the first two segments, then re-join
  const parts = fullDetails.split(",");
  const shortDetails = parts.slice(0, 2).join(",").trim();

  return [
    {
      header: "Port Details",
      isCollapsible: false,
      initialExpanded: true,
      items: [
        {
          label: "ID",
          // value:
          //   externalPortId ??
          //   (externalSubid ? externalSubid.replace(/^\[.*?\]\s*/, "") : "-"),
          value: fetchedData?.subscriptionId ?? "-" 
        },
        { label: "Name", value: fetchedData?.name ?? "-" },
        { label: "Tag", value: fetchedData?.tag ?? "-", type: "tag" },
        {
          label: "Capacity",
          value: fetchedData?.capacityMbps
            ? `${fetchedData.capacityMbps / 1000} Gbps`
            : "-",
        },
        { label: "Location", value: fetchedData?.datacenter?.name ?? "-" },
        ...(fetchedData.lag
          ? [
              {
                label: "LAG",
                value: `See LAG Details`,
                type: "button",
              },
            ]
          : []),
        {
          label: "Cross Connect ID",
          value: fetchedData?.crossConnect?.id
            ? `${fetchedData.crossConnect.providerId} (${shortDetails})`
            : "-",
        },
      ],
    },
    {
      header: "Status",
      isCollapsible: false, // Always expanded
      initialExpanded: true,
      items: [
        {
          label: "Physical Status",
          value:
          fetchedData?.state?.actualState === 'up'
            ? 'Up'
            : fetchedData?.state?.actualState == null
              ? 'Warning'
              : 'Down'
        },
        {
          label: "Service Status",
          value:
            fetchedData?.state?.operationalState === "up"
              ? "Available"
              : "Down",
        },
        // { label: "Traffic Flow", value: fetchedData?.status?.trafficFlow ?? "-" },
        ...(fetchedData.lag
          ? [
              {
                label: "LAG status",
                value: fetchedData.lag.status ?? "-",
              },
            ]
          : []),
        {
          label: "Allocated / Available",
          value:
            allocatedObj && capacity !== 0
              ? `${formatDataRate(allocatedSum)} / ${formatDataRate(available)}`
              : "-",
        },
        { label: "Subscription Details", type: "subtitle" },
        {
          label: "Term",
          value: fetchedData?.subscriptionDetails?.billing_cycle          
            ? `${fetchedData?.subscriptionDetails?.billing_cycle
              } months`
            : "-",
        },
        {
          label: "Auto renew",
          value: fetchedData?.subscriptionDetails?.autoRenew ?? "-",
        },
        {
          label: "Renew Date",
          value: fetchedData?.subscriptionDetails?.renewDate
          ? formatDate(fetchedData?.subscriptionDetails?.renewDate) : '-'
        },
        {
          label: "Subscription Status",
          value: fetchedData.subscriptionDetails?.status
            ? fetchedData.subscriptionDetails?.status.charAt(0).toUpperCase() +
              fetchedData.subscriptionDetails?.status.slice(1)
            : "-",
        },
      ],
    },
    // Only insert the LAG Details card if there's lag data
    ...(fetchedData?.lag
      ? [
          {
            header: "LAG Details",
            isCollapsible: true,
            initialExpanded: true,
            items: [
              { label: "ID", value: fetchedData?.lag?.id ?? "-" },
              { label: "Name", value: fetchedData?.lag?.name ?? "-" },
              {
                label: "Total Capacity",
                value: fetchedData?.lag?.totalCapacity
                  ? `${fetchedData.lag.totalCapacity / 1000} Gbps`
                  : "-",
              },
              { label: "Members", value: fetchedData?.lag?.members ?? "-" },
              { label: "Mode", value: fetchedData?.lag?.mode ?? "-" },
              { label: "LAG Status", value: fetchedData?.lag?.status ?? "-" },
              { label: "Configuration", type: "subtitle" },
              {
                label: "MTU",
                value: fetchedData?.portConfiguration?.mtu ?? "-",
              },
              // {
              // 	label: "Protocol",
              // 	value: fetchedData?.portConfiguration?.protocol ?? "-",
              // },
            ],
          },
        ]
      : []),
    {
      header: "Port Configuration",
      initialExpanded: true,
      items: fetchedData?.portConfiguration
        ? [
            { label: "MTU", value: fetchedData?.portConfiguration?.mtu ?? "-" },
            // {
            // 	label: "Protocol",
            // 	value: fetchedData?.portConfiguration?.encapsulation ?? "-",
            // },
          ]
        : [
            {
              label: "Port Configuration data is unavailable at the moment",
              type: "error",
            },
          ],
    },
    {
      header: "Performance Metrics",
      initialExpanded: true,
      items: fetchedData?.performanceMetrics
        ? [
            {
              label: "CRC Error",
              value: fetchedData?.performanceMetrics?.errors === 0 ? "0" : "-",
            },
          ]
        : [
            {
              label: "Performance Metrics data is unavailable at the moment",
              type: "error",
            },
          ],
    },
  ];
};

//----------Mapping for Connection Details Rendering inside the cards-------------//
export function mapConnectionDetailToSections(
  connectionDetail: any,
  latency?: any
): InternetDetailSection[] {
  // Create the "Connection Details" section
  const data = connectionDetail?.data;
  //------Subnets helpers-------
  //Convert a CIDR prefix (0–32) into dotted-decimal
  function prefixToDottedNetmask(prefix: number): string {
    // create a 32-bit mask, e.g. 0xFFFFFFFF << (32-27) = 0xFFFFFFE0
    const maskUint32 = ~((1 << (32 - prefix)) - 1) >>> 0;
    const octets = [
      (maskUint32 >>> 24) & 0xff,
      (maskUint32 >>> 16) & 0xff,
      (maskUint32 >>> 8) & 0xff,
      maskUint32 & 0xff,
    ];
    return octets.join(".");
  }
  // Compute total IPs from a CIDR prefix
  // function prefixToTotalIPs(prefix: number): number {
  //   return 2 ** (32 - prefix);
  // }
  function prefixToUsableIPs(prefix: number): number {
    const total = 2 ** (32 - prefix);
    // For /31 and /32, special cases
    return prefix >= 31 ? total : total - 2;
  }
  //------

  // Helper to check if at least one value in the row is present (not "-", "", null, undefined)
  const hasAnyValue = (row: Record<string, unknown>) =>
    Object.entries(row).some(([k, v]) =>
      k !== "id" && v != null && v !== "" && v !== "-"
    );
  type P2PRow = {
    id: string;          // "primary" | "secondary"
    network: string;
    mask: string;
    gateway: string;
    peerIp: string;
  };
  const p2p = (data?.p2pAddressing ?? {}) as Partial<{
    mask: string; peerIp: string; gateway: string; network: string;
    maskSecondary: string; peerIpSecondary: string; gatewaySecondary: string; networkSecondary: string;
  }>;
  
  const primaryRow: P2PRow = {
    id: "primary",
    network: p2p.network ?? "-",
    mask:    p2p.mask ?? "-",
    gateway: p2p.gateway ?? "-",
    peerIp:  p2p.peerIp ?? "-",
  };
  
  const secondaryRow: P2PRow = {
    id: "secondary",
    network: p2p.networkSecondary ?? "-",
    mask:    p2p.maskSecondary ?? "-",
    gateway: p2p.gatewaySecondary ?? "-",
    peerIp:  p2p.peerIpSecondary ?? "-",
  };
  
  const p2pRows: P2PRow[] = [];
  if (hasAnyValue(primaryRow))   p2pRows.push(primaryRow);
  if (hasAnyValue(secondaryRow)) p2pRows.push(secondaryRow);

  const parentConnectionValue = (() => {
    // 1) FWaaS takes priority: show the name
    if (data?.fwaas?.name) return String(data.fwaas.name);
    // 2) Port (detail payload uses `port`, not `kontrolEdgePort`)
    if (data?.port?.name) return String(data.port.name);
    // 3) Lag (detail payload uses `lag`; validate form **
    const lag: any = (data as any)?.lag;
    if (lag?.name) return String(lag.name);
    if (lag?.subid) return String(lag.subid);
    // 4) Nothing found
    return "-";
  })();

  const subnetsSection: InternetDetailSection = {
    header: "Public IP Allocation",
    items: [
      {
        label: "IPV4",
        type: "subtitle",
      },
    ],
    table: {
      columns: [
        { header: "IPBlock", accessor: "countryCode" },
        { header: "Network", accessor: "ip" },
        { header: "Mask", accessor: "mask" },
        { header: "Total Available IPs", accessor: "totalIPs" },
      ],
      data: (data?.subnets || []).map((s: any) => {
        const prefix = Number(s.mask);
        return {
          id: s.id,
          ip: s.ip,
          mask: prefixToDottedNetmask(prefix),
          totalIPs: prefixToUsableIPs(prefix),
          countryCode: `${s.mask} / ${s.countryCode}`,
        };
      }),
    },
  };

  const connectionDetailsSection: InternetDetailSection = {
    header: "Connection Details (Internet)",
    isCollapsible: false,
    items: [
      {
        label: "Connection ID",
        value: data?.subscriptionId ?? "-",
      },
      {
        label: "Connection Name",
        value: data?.name,
      },
      {
        label: "TAG",
        value:
          data && Array.isArray(data.tag)
            ? data?.tag.join(", ")
            : data?.tag || "-",
      },
      {
        label: "Location",
        value: data?.port?.datacenter?.displayName ?? "-",
      },
      {
        label: "Capacity",
        value: data ? `${data?.mbps} Mbps` : "-",
      },
      {
        label: "Routing",
        value: data?.routing,
      },
      {
        label: "Parent Connection",
        value: parentConnectionValue
      },
        {
        label: `Latency (${latency?.destination})`,
        value: latency ? `${latency?.value} ${latency?.unit}` : "-" ,
      },
    ],
  };

  // Create the "Subscription Details" section
  const subscriptionDetailsSection: InternetDetailSection = {
    header: "Subscription Details",
    isCollapsible: false,
    items: [
      {
        label: "Service Status",
        value:
        data?.serviceStatus === "up"
          ? "Up"
          : data?.serviceStatus === "warning"
          ? "Warning"
          : "Down",
    },
      {
        label: "Term",
        value: data?.terms ? `${data?.terms} months` : "-",
        type: "text",
      },
      {
        label: "Auto Renew",
        value: data?.subscription?.autoRenew ?? "-",
      },
      {
        label: "Renew Date",
        value: data?.subscription?.renewDate
        ? formatDate(data.subscription.renewDate) : '-'
      },
      {
        label: "Subscription Status",
        value: data?.subscription?.status
          ? data?.subscription?.status.charAt(0).toUpperCase() +
            data?.subscription?.status.slice(1)
          : "-",
      },
      {
        label: "Network Configuration",
        type: "subtitle",
      },
      {
        label: "VLAN ID",
        value: data?.vlan ?? "-",
      },
      {
        label: "Automatic VLAN",
        value: data?.automaticVlan ? "Yes" : "No",
      },
    ],
  };

  const p2pSection: InternetDetailSection = p2pRows.length
  ? {
      header: "P2P Addressing",
      isCollapsible: false,
      items: [], 
      table: {
        columns: [
          { header: "Network",         accessor: "network" as const },
          { header: "Mask",            accessor: "mask" as const },
          { header: "Gateway",         accessor: "gateway" as const },
          { header: "Your Peering IP", accessor: "peerIp" as const },
        ],
        data: p2pRows,
      },
    }
  : {
      header: "P2P Addressing",
      items: [{ label: "No P2P addressing provided", type: "error" }],
    };

  const neighbors: BgpNeighbor[] = Array.isArray(data?.bgpNeighbors)
  ? data!.bgpNeighbors
  : (data?.bgp ? [data.bgp as unknown as BgpNeighbor] : []);

  const bgpSectionLocal: InternetDetailSection = neighbors.length
  ? {
      header: "BGP",
      items: [
        ...(data?.bgpServiceType
          ? [{ label: "Service Type", value: data.bgpServiceType }]
          : []),
        // small summary rollups (unique values across neighbors)
        {
          label: "Peering Types",
          value: Array.from(new Set(neighbors.map(n => n.peeringType))).join(", "),
        },
        {
          label: "Local ASNs",
          value: Array.from(new Set(neighbors.map(n => String(n.localASN)))).join(", "),
        },
        {
          label: "Remote ASNs",
          value: Array.from(new Set(neighbors.map(n => String(n.remoteASN)))).join(", "),
        },
        { label: "Neighbors", type: "subtitle" },
      ],
      table: {
        columns: [
          { header: "Neighbor IP", accessor: "neighborIpAddress" },
          { header: "Remote Router ID", accessor: "remoteRouterId" },
          { header: "Local ASN", accessor: "localASN" },
          { header: "Remote ASN", accessor: "remoteASN" },
          { header: "State", accessor: "state" },
          { header: "Learned Routes", accessor: "prefixesAccepted" },
          { header: "Advertised Routes", accessor: "advertisedPaths" },
          { header: "Peering Type", accessor: "peeringType" },
        ],
        data: neighbors.map((n, idx) => ({
          id: idx,
          source: n.sourceIp ?? "—",
          neighborIpAddress: n.neighborIpAddress ?? "—",
          remoteRouterId: n.remoteRouterId ?? "—",
          localASN: n.localASN ?? "—",
          remoteASN: n.remoteASN ?? "—",
          state: n.state?.isConnected ? "Connected" : "Not connected",
          prefixesAccepted: n.prefixesAccepted ?? "—",
          advertisedPaths: n.advertisedPaths ?? "—",
          peeringType: n.peeringType ?? "—",
        })),
      },
    }
  : {
      header: "BGP",
      items: [
        {
          label: "BGP data is unavailable at the moment",
          type: "error",
        },
      ],
    };
  const performanceMetricsInternet: InternetDetailSection = {
    header: "Performance Metrics",
    items:
      data?.performanceMetrics || latency
        ? [
            {
              label: "CRC Error",
              value: data?.performanceMetrics?.errors === 0 ? "0" : "-",
            },
          ]
        : [
            {
              label: "Performance Metrics data is unavailable at the moment",
              type: "error",
            },
          ],
  };

  const hideFor = ["static"];
  // Return an array of sections
  return [
    connectionDetailsSection,
    subscriptionDetailsSection,
    p2pSection,
    subnetsSection,
    ...(hideFor.includes(data?.routing?.toLowerCase() || "")
      ? []
      : [bgpSectionLocal]),
    performanceMetricsInternet,
  ];
}

// ----------Mapping for Cloud Details Rendering inside the cards-------------//
export function mapCloudDetailToSections(
  detail: CloudConnectivityDetail | undefined
): CCDetailSection[] {
  if (!detail) {
    return [];
  }
  // Compute "parentConnection" exactly as we did in shapeCloudData:
  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  let parentConnectionValue: string = "-";

  if (detail.lag) {
    // If there is a LAG object, prefix with "[LAG]"
    parentConnectionValue = `[LAG]${detail.lag.subid ?? "-"}`;
  } else if (detail.port) {
    // If there is a Port object, prefix with "[PORT]"
    // Some newer payloads put the port subscriptionId in detail.port.subid,
    // but if that's missing,fall back to detail.port.name.
    const portSubid = (detail.port as any).subid ?? detail.port.name;
    parentConnectionValue = `[PORT]${portSubid ?? "-"}`;
  }
  const getProviderIcon = (
    provider: string | undefined | null
  ): React.ComponentType<React.SVGProps<SVGSVGElement>> | undefined => {
    if (!provider) return undefined;
  
    const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
      consoleConnect: ConsoleConnectIcon,
      equinix: EquinixIcon,
      megaport: MegaportIcon,
    };
  
    return iconMap[provider];
  };

  const formatProviderName = (provider: string | undefined | null): string => {
    if (!provider) return "-";
  
    const map: Record<string, string> = {
      consoleConnect: "Console Connect",
      equinix: "Equinix",
      megaport: "Megaport",
    };
  
    return map[provider] || provider.charAt(0).toUpperCase() + provider.slice(1);
  };

  const connectionDetailsSection: CCDetailSection = {
    header: "Connection Details (Cloud Connectivity)",
    isCollapsible: false,
    items: [
      {
        label: "Connection ID",
        value: detail?.subscriptionId ?? "-",
      },
      {
        label: "Provider",
        value: formatProviderName(detail?.provider) ?? "-",
        Icon: getProviderIcon(detail?.provider),
      },
      {
        label: "Connection Name",
        value: detail?.name ?? "-",
      },
      {
        label: "TAG",
        value:
          Array.isArray(detail?.tag) && detail?.tag.length > 0
            ? detail?.tag.join(", ")
            : detail?.tag || "-",
      },
      {
        label: "Cloud Account",
        value:
          detail?.cloud && detail?.account != null
            ? `${detail?.cloud} (${detail?.account})`
            : "-",
      },
      {
        label: "Cloud Region",
        value: detail?.providerInfo?.cloudRegion ?? "-",
      },
      {
        label: "Capacity",
        value: detail?.mbps != null ? `${detail.mbps} Mbps` : "-",
      },
      {
        label: "Cloud Connection Id",
        value: detail?.providerInfo?.connectionCloudId ?? "-",
      },
      {
        label: "Parent Connection",
        value: parentConnectionValue,
      },
      {
        label: "Origin Location",
        value: detail?.port?.datacenter?.name ?? "-",
      },
    ],
  };

  const subscriptionDetailsSection: CCDetailSection = {
    header: "Subscription Details",
    isCollapsible: false,
    items: [
      {
        label: "Term",
        value:
          detail?.subscription?.contractMonths != null
            ? `${detail?.subscription.contractMonths} months`
            : "-",
        type: "subtitle",
      },
      {
        label: "Auto Renew",
        value: detail.subscription?.autoRenew ?? "-",
      },
      {
        label: "Renew Date",
        value: detail?.subscription?.renewDate
        ? formatDate(detail.subscription.renewDate) : '-'
      },
      {
        label: "Subscription Status",
        value: detail?.subscription?.status
          ? detail?.subscription?.status.charAt(0).toUpperCase() +
            detail?.subscription?.status.slice(1)
          : "-",
      },
      {
        label: "Network Configuration",
        type: "subtitle",
      },
      {
        label: "Source VLAN ID",
        value: detail?.providerInfo?.sourceVLAN ?? "-",
      },
      {
        label: "Automatic VLAN",
        value: detail?.automaticVlan ? "Yes" : "No",
      },
      {
        label: "Cloud VLAN ID",
        value: detail?.providerInfo?.cloudVLAN ?? "-",
      },
    ],
  };

  const performanceSection: CCDetailSection = {
    header: "Performance Metrics",
    isCollapsible: false,
    items: [
      {
        label: "Service status",
        value: detail?.providerInfo?.state
          ? (() => {
              const raw = detail.providerInfo.state;
              const lower = raw.toLowerCase();
              return lower.charAt(0).toUpperCase() + lower.slice(1);
            })()
          : "-",
      },
      {
        label: "CRC Error",
        value: detail?.performanceMetrics?.errors === 0 ? "0" : "-",
      },
    ],
  };

  return [
    connectionDetailsSection,
    subscriptionDetailsSection,
    performanceSection,
  ];
}

// ----------Mapping for Cloud Details Rendering inside the cards-------------//
export function mapDatacenterDetailToSections(
  datacenterDetail: any,
  latency?: any
): DatacenterDetailSection[] {
  // Create the "Connection Details" section
  const data = datacenterDetail?.data;
  const tableData =
    data && Array.isArray(data.ports)
      ? data.ports.map(
          (port: { datacenter: { displayName: string }; name: string }) => ({
            location: port?.datacenter?.displayName || "-",
            portName: port?.name || "-",
          })
        )
      : [];

  //------
  const connectionDetailsSection: DatacenterDetailSection = {
    header: "Data Center Details",
    isCollapsible: false,
    items: [
      {
        label: "DCI ID",
        value: data?.subscriptionId ?? "-",
      },
      {
        label: "Data Center Name",
        value: data?.name ?? "-",
      },
      {
        label: "TAG",
        value:
          data && Array.isArray(data.tag)
            ? data.tag.join(", ")
            : data?.tag || "-",
      },
      {
        label: "Capacity",
        value: data ? `${data.mbps} Mbps` : "-",
      },
      {
        label: "Relation with location and port",
        type: "subtitle",
      },
    ],
    table: {
      columns: [
        { header: "Location", accessor: "location" },
        { header: "Port", accessor: "portName" },
      ],
      data: tableData,
    },
  };

  // Create the "Subscription Details" section
  const subscriptionDetailsSection: DatacenterDetailSection = {
    header: "Subscription Details",
    isCollapsible: false,
    items: [
      {
        label: "Service Status",
        value:
          data?.serviceStatus === "up"
            ? "Available"
            : "Down",
      },
      {
        label: "Term",
        value: data?.terms ? `${data?.terms} months` : "-",
        type: "subtitle",
      },
      {
        label: "Auto Renew",
        value: data?.subscription?.autoRenew ?? "-",
      },
      {
        label: "Renew Date",
        value: data?.subscription?.renewDate
        ? formatDate(data.subscription.renewDate) : '-'
      },
      {
        label: "Subscription Status",
        value: data?.subscription?.status
          ? data?.subscription?.status.charAt(0).toUpperCase() +
            data?.subscription?.status.slice(1)
          : "-",
      },
      {
        label: "Network Configuration",
        type: "subtitle",
      },
      {
        label: "VLAN ID",
        value: data?.vlan ?? "-",
      },
      {
        label: "Automatic VLAN",
        value: data?.automaticVlan ? "Yes" : "No",
      },
    ],
  };

  const performanceMetricsInternet: DatacenterDetailSection = {
    header: "Performance Metrics",
    items:
      data?.performanceMetrics || latency
        ? [
            {
              label: "CRC Error",
              value: data?.performanceMetrics?.errors === 0 ? "0" : "-",
            },
          ]
        : [
            {
              label: "Performance Metrics data is unavailable at the moment",
              type: "error",
            },
          ],
  };
  // Return an array of sections
  return [
    connectionDetailsSection,
    subscriptionDetailsSection,
    performanceMetricsInternet,
  ];
}
